import React, { useEffect, useRef } from 'react';
import { Ico } from './icons';

export function useFocusTrap(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prevFocus = document.activeElement;
    const focusable = el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first) first.focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab' || !first) return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last && last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => {
      el.removeEventListener('keydown', handleKeyDown);
      if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus();
    };
  }, [ref]);
}

export function UnsavedModal({ onSave, onDiscard, onClose }) {
  const modalRef = useRef(null);
  useFocusTrap(modalRef);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="unsaved-modal-title">
      <div className="modal modal--sm" onClick={e => e.stopPropagation()} ref={modalRef}>
        <div className="modal__header">
          <h2 className="modal__title" id="unsaved-modal-title">Save changes before leaving?</h2>
          <button className="btn btn--icon" onClick={onClose} aria-label="Close dialog"><Ico.Close width="16" height="16"/></button>
        </div>
        <div className="modal__footer">
          <button className="btn btn--tertiary btn--md" onClick={onDiscard}>Don't save</button>
          <button className="btn btn--tertiary btn--md" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--md" onClick={onSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

export function ConflictModal({ onReload, onSaveMine, onClose }) {
  const modalRef = useRef(null);
  useFocusTrap(modalRef);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="conflict-modal-title">
      <div className="modal modal--sm" onClick={e => e.stopPropagation()} ref={modalRef}>
        <div className="modal__header">
          <h2 className="modal__title" id="conflict-modal-title">This document changed somewhere else</h2>
          <button className="btn btn--icon" onClick={onClose} aria-label="Close dialog"><Ico.Close width="16" height="16"/></button>
        </div>
        <div className="modal__body">
          <p>Someone — for example an AI agent — saved changes to this document after you opened it. Reload to see the latest version (your unsaved edits will be lost), or save your version to replace those changes.</p>
        </div>
        <div className="modal__footer">
          <button className="btn btn--tertiary btn--md" onClick={onReload}>Reload</button>
          <button className="btn btn--primary btn--md" onClick={onSaveMine}>Save my version</button>
        </div>
      </div>
    </div>
  );
}

export function ExportModal({ doc, onClose, onExport }) {
  const modalRef = useRef(null);
  useFocusTrap(modalRef);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
      <div className="modal modal--sm" onClick={e=>e.stopPropagation()} ref={modalRef}>
        <div className="modal__header">
          <h2 className="modal__title" id="export-modal-title">Save before exporting?</h2>
          <button className="btn btn--icon" onClick={onClose} aria-label="Close dialog"><Ico.Close width="16" height="16"/></button>
        </div>
        <div className="modal__body">
          <p>"{doc.name}" has unsaved changes. They'll be saved before the PDF is generated.</p>
        </div>
        <div className="modal__footer">
          <button className="btn btn--ghost btn--md" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--md" onClick={()=>onExport('all', Object.keys(doc.pages))}>Save & Export</button>
        </div>
      </div>
    </div>
  );
}
